/**
 * Browser-side structured logger. Same surface as `src/lib/logger.ts` so
 * call sites read identically.
 *
 * After the Sentry migration:
 *   • `error` → `Sentry.captureException` (browser Sentry is the exception
 *     system-of-record; the Supabase user is attached by the Sentry init in
 *     `src/instrumentation-client.ts`).
 *   • `warn` / `info` / `debug` → console only. Browser non-error logs are NOT
 *     shipped anywhere — they stay in DevTools. (Server + worker logs go to
 *     Sentry Logs; client log volume is intentionally kept out to control
 *     cost.) PostHog keeps only product analytics, never logs.
 *
 * Telemetry gating piggy-backs on the Sentry browser client: it only inits in
 * production (or with `NEXT_PUBLIC_POSTHOG_FORCE_ENABLE=1`) AND when a
 * `NEXT_PUBLIC_SENTRY_DSN` is set (see `instrumentation-client.ts`). In local
 * development `Sentry.getClient()` is `undefined` and the error path
 * short-circuits to `console.error` only — nothing leaves the browser.
 *
 * Server callers must use `@/lib/logger` instead.
 */

import * as Sentry from '@sentry/nextjs';

type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogContext {
  /** An error or unknown caught value. */
  err?: unknown;
  /** Additional structured fields. */
  [key: string]: unknown;
}

function consoleSink(
  level: ClientLogLevel,
  msg: string,
  context?: ClientLogContext,
): void {
  const fn = level === 'error' || level === 'warn' ? console.error : console.log;
  if (context && Object.keys(context).length > 0) {
    fn(`[${level.toUpperCase()}] ${msg}`, context);
  } else {
    fn(`[${level.toUpperCase()}] ${msg}`);
  }
}

function emit(
  level: ClientLogLevel,
  msg: string,
  context?: ClientLogContext,
): void {
  // Always log to the browser console — useful in production DevTools too.
  consoleSink(level, msg, context);

  // Only errors ship off the browser; non-error logs stay console-only.
  if (level !== 'error') return;

  // SSR safety + dev gate: when Sentry isn't initialized (dev / no DSN),
  // `getClient()` is undefined — skip so we don't pretend to report.
  if (typeof window === 'undefined') return;
  if (!Sentry.getClient()) return;

  const { err: _omit, ...rest } = context ?? {};
  const errInstance =
    context?.err instanceof Error
      ? context.err
      : new Error(typeof msg === 'string' ? msg : 'client error');

  Sentry.captureException(errInstance, {
    extra: {
      log_message: msg,
      ...rest,
    },
  });
}

export interface ScopedClientLogger {
  debug(msg: string, context?: ClientLogContext): void;
  info(msg: string, context?: ClientLogContext): void;
  warn(msg: string, context?: ClientLogContext): void;
  error(msg: string, context?: ClientLogContext): void;
}

export const clientLogger = {
  debug(msg: string, context?: ClientLogContext): void {
    emit('debug', msg, context);
  },
  info(msg: string, context?: ClientLogContext): void {
    emit('info', msg, context);
  },
  warn(msg: string, context?: ClientLogContext): void {
    emit('warn', msg, context);
  },
  error(msg: string, context?: ClientLogContext): void {
    emit('error', msg, context);
  },
  /**
   * Returns a child logger that prepends a tag to every message and merges
   * default context fields. Matches the server `logger.scope` API.
   */
  scope(
    tag: string,
    defaults?: Omit<ClientLogContext, 'err'>,
  ): ScopedClientLogger {
    return {
      debug(msg, ctx) {
        emit('debug', `[${tag}] ${msg}`, { ...defaults, ...ctx });
      },
      info(msg, ctx) {
        emit('info', `[${tag}] ${msg}`, { ...defaults, ...ctx });
      },
      warn(msg, ctx) {
        emit('warn', `[${tag}] ${msg}`, { ...defaults, ...ctx });
      },
      error(msg, ctx) {
        emit('error', `[${tag}] ${msg}`, { ...defaults, ...ctx });
      },
    };
  },
};
